import { motion } from "framer-motion";
import { ArrowRight, Phone, Sparkles, Star } from "lucide-react";
import { CONTACT } from "./constants";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="anasayfa"
      data-testid="hero-section"
      className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden"
    >
      {/* background layers */}
      <div className="absolute inset-0 ab-grid-bg opacity-60" aria-hidden />
      <div className="absolute inset-0 ab-radial-glow" aria-hidden />
      <div
        className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
        style={{
          background:
            "radial-gradient(circle at center, #2D3FFF 0%, transparent 70%)",
        }}
        aria-hidden
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0}
            >
              <span className="ab-chip">
                <Sparkles className="h-3.5 w-3.5" />
                Dijital Çözümler Ajansı
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] text-[var(--ab-navy)]"
            >
              Dijitalde Büyümenin
              <br />
              <span className="ab-text-gradient">Profesyonel</span> Yolu.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed"
            >
              İşletmenize özel <strong className="text-[var(--ab-navy)]">web sitesi tasarımı</strong>, hedefli{" "}
              <strong className="text-[var(--ab-navy)]">Google &amp; Instagram reklam yönetimi</strong>,{" "}
              <strong className="text-[var(--ab-navy)]">WhatsApp otomasyonu</strong>, randevu ve sipariş takip
              sistemleri ile rakiplerinizin bir adım önüne geçin.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <a
                href={CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="ab-btn-primary inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
              >
                WhatsApp&apos;tan Ulaş
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={CONTACT.phoneLink}
                data-testid="hero-phone-btn"
                className="ab-btn-secondary inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
              >
                <Phone className="h-4 w-4" />
                Hemen Ara: {CONTACT.phoneDisplay}
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-10 flex flex-wrap items-center gap-6 text-sm text-slate-500"
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={n}
                      className="w-7 h-7 rounded-full border-2 border-white"
                      style={{
                        background: `linear-gradient(135deg, hsl(${n * 60} 70% 60%), hsl(${n * 60 + 40} 70% 55%))`,
                      }}
                    />
                  ))}
                </div>
                <span>
                  <strong className="text-[var(--ab-navy)]">120+</strong> mutlu müşteri
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className="h-4 w-4 fill-amber-400 text-amber-400"
                  />
                ))}
                <span className="ml-1">
                  <strong className="text-[var(--ab-navy)]">4.9/5</strong> müşteri puanı
                </span>
              </div>
            </motion.div>
          </div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative">
              <div
                className="absolute -inset-6 rounded-[2rem] blur-2xl opacity-40"
                style={{
                  background:
                    "linear-gradient(135deg, #2D3FFF, #5A3FFF)",
                }}
                aria-hidden
              />
              <div className="relative rounded-3xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(10,19,48,0.30)] overflow-hidden">
                <img
                  src="/hero-banner.jpg"
                  alt="AjansBilir - Profesyonel Web Site Yapımı"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
